import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  File,
  Image,
  Film,
  Music,
  X,
  CheckCircle,
  AlertCircle,
  CloudUpload
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/utils/api';
import clsx from 'clsx';
import type { UploadTask } from '@/types';

const FILE_TYPE_ICONS = {
  image: Image,
  video: Film,
  audio: Music,
  file: File
};

function getFileType(file: File): string {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'file';
}

function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const taskId = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const initResult = await api.initUpload(
        file.name,
        file.type,
        file.size
      );

      if (file.size <= 20 * 1024 * 1024) {
        await fetch(initResult.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        return api.completeUpload(
          initResult.uploadId,
          getFileType(file)
        );
      }

      const CHUNK_SIZE = 15 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        await api.uploadChunk(initResult.uploadId, i, totalChunks, chunk);

        setTasks(prev => prev.map(t =>
          t.id === taskId
            ? { ...t, progress: Math.round(((i + 1) / totalChunks) * 100) }
            : t
        ));
      }

      return api.completeUpload(
        initResult.uploadId,
        getFileType(file)
      );
    },
    onSuccess: (_, file) => {
      setTasks(prev => prev.map(t =>
        t.filename === file.name
          ? { ...t, status: 'completed', progress: 100 }
          : t
      ));
    },
    onError: (error, file) => {
      setTasks(prev => prev.map(t =>
        t.filename === file.name
          ? { ...t, status: 'failed', error: error.message }
          : t
      ));
    }
  });

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const newTasks: UploadTask[] = Array.from(files).map(file => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      filename: file.name,
      size: file.size,
      status: 'pending',
      progress: 0
    }));

    setTasks(prev => [...prev, ...newTasks]);

    Array.from(files).forEach(file => {
      uploadMutation.mutate(file);
    });
  };

  const removeTask = (taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/files')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">上传文件</h1>
        </div>
      </div>

      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={clsx(
          'card p-12 mb-6 border-2 border-dashed cursor-pointer transition-all',
          dragOver
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <CloudUpload className="w-8 h-8 text-primary-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            拖放文件到此处或点击选择
          </h3>
          <p className="text-gray-500">
            支持图片、视频、音频、PDF 等文件
          </p>
          <p className="text-sm text-gray-400 mt-2">
            最大支持 50MB 文件
          </p>
        </div>
      </div>

      {tasks.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">
              上传队列 ({tasks.length})
            </h3>
          </div>

          <div className="divide-y divide-gray-100">
            {tasks.map((task) => {
              const Icon = FILE_TYPE_ICONS[getFileType({ type: '' } as File) as keyof typeof FILE_TYPE_ICONS] || File;

              return (
                <div key={task.id} className="px-4 py-3 flex items-center gap-4">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-gray-900 truncate">
                        {task.filename}
                      </p>
                      <div className="flex items-center gap-2">
                        {task.status === 'completed' && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        {task.status === 'failed' && (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        {task.status !== 'completed' && task.status !== 'failed' && (
                          <button
                            onClick={() => removeTask(task.id)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <X className="w-4 h-4 text-gray-400" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">
                        {formatFileSize(task.size)}
                      </span>

                      {task.status === 'uploading' && (
                        <div className="flex-1 flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-600 transition-all"
                              style={{ width: `${task.progress}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-500">
                            {task.progress}%
                          </span>
                        </div>
                      )}

                      {task.status === 'failed' && (
                        <span className="text-sm text-red-500">
                          {task.error || '上传失败'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">上传提示</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>文件将自动根据类型挂载为 Notion 块（图片、视频、音频、PDF 等）</li>
          <li>大文件（&gt;20MB）将使用分片上传以提高稳定性</li>
          <li>上传速度受 Notion API 限制，请耐心等待</li>
        </ul>
      </div>
    </div>
  );
}